import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AssetGrid, SearchBar, SmartSearchChips } from '@shared/components'
import { useAssetSearch } from '@shared/hooks/useAssetSearch'
import { useInfiniteScroll } from '@shared/hooks/useInfiniteScroll'
import { useSelection } from '@shared/state/SelectionContext'
import { FilterBar } from '../components/FilterBar'
import { useAuth } from '@shared/state/AuthContext'
import { loadFilterColors } from '@shared/services/filterColors'
import { libraryDef } from '@shared/data/libraries'
import type { Asset, LibraryKey } from '@shared/types'
import { PictureTagsDialog } from '../components/PictureTagsDialog'
import { PictureViewer } from '../components/PictureViewer'

/**
 * A library: Audit, Visual Language or Design Works.
 *
 * One page for all three, because they are the same thing with different
 * pictures in -- search, filter, pick, send to Miro. What differs is said by
 * the library's definition (src/shared/data/libraries.ts): Audit has the
 * packaging buttons, Visual Language has one row of tags (several chosen means
 * pictures carrying all of them), Design Works has its own categories.
 *
 * Search first, then the filter, then the pictures. There used to be a title
 * and a row of statistics above all of it; they were the first thing on screen
 * and the last thing anybody read, so they are gone — the page announces itself
 * by what it shows.
 */
export function LibraryPage({ library = 'audit' }: { library?: LibraryKey }) {
  const def = libraryDef(library)
  const { user } = useAuth()
  // The filter's colours on the black skin, as an administrator last synced
  // them (the tuner that set them was taken out on 2026-10-02).
  useEffect(() => {
    void loadFilterColors()
  }, [])
  const {
    assets, total, tags, groups, text, setText, activeTagIds, toggleTag, clearFilters,
    loading, error, loadingMore, hasMore, loadMore, packaging, setPackaging, hasFilters,
    fetchMatching, reloadTags, tagsById,
    plan, understanding, dropPiece,
  } = useAssetSearch(60, library, { smart: true })
  const navigate = useNavigate()
  /**
   * Visual Language: Mood board beside the search box (Huanrui, 2026-10-12)
   * lays out what is selected. The search words become the board's title and
   * the tags the selected pictures share most become its words.
   */
  const openMoodboard = () => {
    const counts = new Map<string, number>()
    for (const asset of selectedAssets) {
      for (const id of asset.tagIds) counts.set(id, (counts.get(id) ?? 0) + 1)
    }
    const keywords = [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => tagsById.get(id)?.name)
      .filter((name): name is string => Boolean(name))
      .slice(0, 3)
    navigate('/moodboard', { state: { fromSelection: true, title: text.trim(), keywords } })
  }
  // Administrators: hold a picture for three seconds to edit its tags
  // (Huanrui, 2026-10-12). Every library on this page; Amazon has its own.
  const isAdmin = user?.role === 'admin'
  const [tagging, setTagging] = useState<Asset | null>(null)
  /** A picture opened larger from its card's View larger button (2026-10-15). */
  const [viewing, setViewing] = useState<Asset | null>(null)
  const { selectedIds, selectedAssets, toggle, select, deselect, selectMany, deselectMany } = useSelection()

  // The rest of the library arrives as it is scrolled to. The button below is
  // not a fallback nobody sees: it is there for keyboard and screen-reader use,
  // where there is no scrolling to observe.
  // Re-armed after every page (not while one is loading), so a marker still in
  // view after a short page asks for the next one instead of waiting for a scroll.
  const marker = useInfiniteScroll(hasMore && !loading && !loadingMore, loadMore)

  // Select all works on what the filters have left on screen, not on the whole
  // library — picking "all" while looking at 12 results should not quietly
  // select nine hundred.
  const shownIds = useMemo(() => assets.map((asset) => asset.id), [assets])
  const [selecting, setSelecting] = useState(false)

  // The library is long enough to get lost in, so offer the way back up once
  // there is a way up to go back to.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 700)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  /**
   * How many can be picked in one go.
   *
   * "Select all" used to mean the sixty images that happened to have been
   * scrolled to, which is not what anybody reads it as. It now means all of the
   * results — and since reaching for all four hundred is rarely what is wanted
   * either, the smaller round numbers are offered beside it.
   *
   * A step is offered only when it is smaller than the result set; asking for
   * 200 of 60 is the same button as All, twice.
   */
  const selectSteps = useMemo(() => [10, 20, 50, 100, 200].filter((n) => n < total), [total])

  async function selectCount(count: number) {
    setSelecting(true)
    try {
      selectMany(await fetchMatching(count))
    } finally {
      setSelecting(false)
    }
  }

  return (
    <section className="web-library" id="library">
      <div className={`web-library__search${library === 'visual-language' ? ' web-library__search--with' : ''}`}>
        <SearchBar value={text} onChange={setText} placeholder={def.searchHint} />
        {library === 'visual-language' ? (
          <button
            type="button"
            className="web-library__moodboard"
            onClick={openMoodboard}
            disabled={selectedIds.length === 0}
            title={selectedIds.length === 0 ? 'Select pictures first' : `Lay out the ${selectedIds.length} selected pictures`}
          >
            Create mood board
          </button>
        ) : null}
      </div>
      <SmartSearchChips plan={plan} understanding={understanding} onDrop={dropPiece} />

      <FilterBar
        tags={tags}
        groups={groups}
        activeTagIds={activeTagIds}
        onToggleTag={toggleTag}
        flat={!def.hasCategories}
        packaging={def.hasPackaging ? packaging : undefined}
        onPackagingChange={def.hasPackaging ? setPackaging : undefined}
        hasFilters={hasFilters}
        onClear={clearFilters}
        unfinished={def.unfinishedGroups}
      />

      {error ? <div className="ww-error">{error}</div> : null}

      <div className="web-library__toolbar">
        {total > 0 ? (
          <div className="web-select" role="group" aria-label="Select images">
            <span className="web-select__label">{selecting ? 'Selecting…' : 'Select'}</span>
            {selectSteps.map((count) => (
              <button
                key={count}
                type="button"
                className="web-select__step"
                disabled={selecting}
                onClick={() => void selectCount(count)}
              >
                {count}
              </button>
            ))}
            <button
              type="button"
              className="web-select__step"
              disabled={selecting}
              onClick={() => void selectCount(total)}
            >
              All
            </button>
            {/* The number the All button used to carry, now stated rather than
                built into a label — a label that was wrong the moment the
                library outgrew the cap behind it. It counts what the current
                filter matches, not the whole library, because that is what All
                would select. */}
            <span className="web-select__of">of {total}</span>
          </div>
        ) : null}

        {selectedIds.length > 0 ? (
          <button
            type="button"
            className="ww-btn ww-btn--ghost ww-btn--sm"
            onClick={() => deselectMany(shownIds.concat(selectedIds))}
          >
            Deselect all
          </button>
        ) : null}
      </div>

      {/* The pictures straight on the page, edge to edge with the search and
          the toolbar above, the way the Amazon page shows its products
          (Huanrui, 2026-10-02). They used to stand on a white plate. */}
      <div className="web-results">
        <AssetGrid
          assets={assets}
          selectedIds={selectedIds}
          onToggle={toggle}
          onPaint={(asset, wanted) => (wanted ? select(asset) : deselect(asset.id))}
          onLongPress={isAdmin ? setTagging : undefined}
          onView={setViewing}
          loading={loading}
          emptyHint={
            hasFilters
              ? 'Nothing carries all of these filters. Clear them and start again.'
              : def.emptyHint
          }
        />

        {!loading && assets.length === 0 && hasFilters ? (
          <div className="web-library__more">
            <button type="button" className="ww-btn ww-btn--secondary ww-btn--sm" onClick={clearFilters}>
              Clear filters
            </button>
          </div>
        ) : null}

        {hasMore && !loading ? (
          <div className="web-library__more" ref={marker}>
            <button
              type="button"
              className="ww-btn ww-btn--secondary ww-btn--sm"
              disabled={loadingMore}
              onClick={loadMore}
            >
              {/* The number is dropped rather than shown as zero. At exactly
                  one full page the server still hands back a cursor — it
                  cannot know there is nothing after — so this button appears
                  with nothing left to fetch, and "Load 0 more" is a worse
                  thing to read than "Load more". */}
              {loadingMore
                ? 'Loading…'
                : total - assets.length > 0
                  ? `Load ${Math.min(60, total - assets.length)} more`
                  : 'Load more'}
            </button>
          </div>
        ) : null}
      </div>

      {viewing ? (
        <PictureViewer
          src={viewing.thumbnailPath || viewing.originalPath}
          full={viewing.originalPath}
          onClose={() => setViewing(null)}
        />
      ) : null}

      {tagging ? (
        <PictureTagsDialog
          library={library}
          asset={tagging}
          onClose={(changed) => {
            setTagging(null)
            // The filter's counts move with what was just changed.
            if (changed) reloadTags()
          }}
        />
      ) : null}

      {scrolled ? (
        <button
          type="button"
          className={`web-totop${selectedIds.length > 0 ? ' web-totop--raised' : ''}`}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <span aria-hidden="true">&#8593;</span>
          Top
        </button>
      ) : null}
    </section>
  )
}
